import React, { useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import {
  creditWalletOperationCompleted,
  useWalletBalance,
  walletCredits,
} from '../creditWallet/WalletBalanceContext.js';
import {
  CREDIT_CONNECTION_PRICE_CREDITS,
  checkTierCapacity,
  deriveCurrentTier,
  newPurchaseOperationId,
  purchaseConnectionWithCredits,
} from '../utils/creditWalletRpc.js';
import { purchaseDeniedMessage, purchaseErrorMessage } from '../utils/creditPurchaseMessages.js';

// Phase 5 credit-connection purchase CTA (plan 05 §1), shared by the
// access-code management surface and the per-patient connect surface.
//
// FROZEN contract (migration 20260825_credit_paid_connections.sql):
// purchase_connection_with_credits(p_client_email text, p_operation_id uuid).
// One operation id per purchase intent, reused across retries (idempotent);
// a new intent — a different email or a completed purchase — gets a fresh id.
// check_tier_capacity runs before the purchase and shows the denial
// arithmetic; the RPC stays authoritative (soft-denial reasons carry the
// same arithmetic). The purchase debits CREDIT_CONNECTION_PRICE_CREDITS from
// the wallet, so the wallet refresh event is dispatched on success.
//
// The debit is behind a confirmation dialog: the button click validates the
// email and runs the capacity pre-check, and only the dialog's Confirm
// generates an operation id and calls the RPC. Cancel touches nothing.
//
// Denial/error copy lives in utils/creditPurchaseMessages.js — one place for
// every purchase surface, including the New-Client form's connect step.

const CreditConnectionPurchase = ({
  clientEmail,
  userId,
  onPurchased,
  buttonLabel = 'Add client by email',
  sx,
}) => {
  // `checking` covers the pre-check (button spinner); `purchasing` covers the
  // RPC (dialog spinner). Either one blocks the dialog from closing.
  const [checking, setChecking] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [open, setOpen] = useState(false);
  // The email snapshotted when the dialog opened, so the dialog shows and
  // charges exactly the address the therapist confirmed.
  const [pendingEmail, setPendingEmail] = useState('');
  // One error slot: rendered inside the dialog while it is open, under the
  // button otherwise.
  const [error, setError] = useState(null);
  // The operation id for the current purchase intent, plus the email it was
  // generated for. Cleared on success or on an id conflict, so the next
  // intent starts fresh.
  const operationIdRef = useRef(null);
  const intentEmailRef = useRef(null);

  // Context default is {wallet: null}, so the balance line simply disappears
  // when no provider is mounted.
  const { wallet } = useWalletBalance() || {};
  const credits = walletCredits(wallet);
  const price = CREDIT_CONNECTION_PRICE_CREDITS;
  const loading = checking || purchasing;

  // Button click: validate, pre-check, then ask. No debit happens here.
  const handleOpen = async () => {
    const email = (clientEmail || '').trim();
    // Mirrors the RPC's own guard (length 3–320, at least one character
    // before the @; SQL position() is 1-based, indexOf is 0-based).
    if (email.length < 3 || email.length > 320 || email.indexOf('@') < 1) {
      setError('Enter a valid client email (3–320 characters, containing an @).');
      return;
    }
    setChecking(true);
    setError(null);
    try {
      // Capacity pre-check: the cheapest denial first, with the arithmetic
      // the purchase would have returned. A tier we cannot derive stays
      // silent — the RPC is authoritative.
      const tier = await deriveCurrentTier(userId);
      if (tier) {
        const { data: capacity, error: capacityError } = await checkTierCapacity(tier);
        if (!capacityError && capacity && capacity.allowed === false) {
          setError(
            capacity.arithmetic || 'Your plan has reached its limit of credit-paid connections.',
          );
          return;
        }
      }
      setPendingEmail(email);
      setOpen(true);
    } catch (thrown) {
      // supabase-js resolves with {error}; a thrown rejection is a network
      // failure. Say so rather than silently returning the button to idle.
      setError(purchaseErrorMessage(thrown));
    } finally {
      setChecking(false);
    }
  };

  // Cancel/backdrop/Escape: no RPC, no operation id; blocked while busy. Any
  // error moves from the dialog to under the button until the next click.
  const handleClose = () => {
    if (loading) return;
    setOpen(false);
  };

  // Dialog Confirm: the only place an operation id is generated and the RPC
  // is called. Errors keep the dialog open so a retry reuses the same id.
  const handleConfirm = async () => {
    const email = pendingEmail;
    setPurchasing(true);
    setError(null);
    try {
      if (!operationIdRef.current || intentEmailRef.current !== email) {
        operationIdRef.current = newPurchaseOperationId();
        intentEmailRef.current = email;
      }
      const operationId = operationIdRef.current;

      const { data, error: rpcError } = await purchaseConnectionWithCredits(email, operationId);
      if (rpcError) {
        if (rpcError.code === '22023' && (rpcError.message || '').includes('conflict')) {
          // The id is burned for this email: the next attempt needs a fresh one.
          operationIdRef.current = null;
        }
        setError(purchaseErrorMessage(rpcError));
        return;
      }
      if (data?.purchased) {
        // Intent complete — a fresh purchase needs a fresh operation id.
        operationIdRef.current = null;
        intentEmailRef.current = null;
        creditWalletOperationCompleted();
        setOpen(false);
        onPurchased?.(data);
        return;
      }
      setError(purchaseDeniedMessage(data) || 'The credit purchase could not be completed.');
    } catch (thrown) {
      // Nothing was debited if the call never reached the RPC; the same
      // operation id is safe to retry.
      setError(purchaseErrorMessage(thrown));
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={handleOpen}
        disabled={loading}
        startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}
        sx={{
          backgroundColor: '#29C0BB',
          color: '#FFFFFF',
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#1FA09B' },
          ...sx,
        }}
      >
        {checking ? 'Checking…' : buttonLabel}
      </Button>
      {error && !open && (
        <span style={{ color: '#d32f2f', fontSize: '0.8rem', display: 'block', marginTop: 8 }}>
          {error}
        </span>
      )}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add this client with credits?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Connect <strong>{pendingEmail}</strong> for {price} credits/month.{' '}
            {price} credits will be deducted from your credit wallet now for this
            month&apos;s connection. The connection is active from payment; the
            client must still consent to sharing in their MindVault app.
          </DialogContentText>
          {credits !== null && (
            <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
              Your balance: {credits} credits
              {credits >= price && ` · ${credits - price} after this purchase`}.
            </Typography>
          )}
          {credits !== null && credits < price && (
            // A hint only: the RPC is the authority on whether the wallet
            // can cover the debit, so Confirm stays enabled.
            <Typography variant="body2" sx={{ color: '#d32f2f', mt: 0.5 }}>
              You need {price} credits for this connection.
            </Typography>
          )}
          {error && (
            <Typography variant="body2" sx={{ color: '#d32f2f', mt: 1.5 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={loading}
            startIcon={purchasing ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{
              backgroundColor: '#29C0BB',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#1FA09B' },
            }}
          >
            {purchasing ? 'Adding…' : `Confirm and deduct ${price} credits`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreditConnectionPurchase;
